import React, { type JSX, createRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { pushToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { ModelType } from '../../common/enums.js';
import { fetchAddOnRules } from '../../common/fetch.js';
import { findActiveAddonAssociation, newAddon } from '../../common/utils/addOnUtils.js';
import { AnalyticsListNames, getAddToCartAnalyticsEvent } from '../../common/utils/analyticsUtils';
import { getOfferCampaignAssociationAndPromotion } from '../../common/utils/campaignUtils.js';
import { deepEqual, isDefined } from '../../common/utils/objectUtils.js';
import { eurosToCents } from '../../common/utils/priceUtils.js';
import { clampQuantity } from '../../common/utils/quantityUtils';
import { isCommitmentPeriodActive } from '../../common/utils/subscriptionUtils';
import { AddOnPurpose, SalesType } from '../../generated/api/models.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { filterByOnlineModelCodes } from '../../public/site/onlineModel/onlineModelService.js';
import { Grid } from '../Grid/Grid.js';
import { getOfferByCommercialProductCode } from '../HorizontalSubscriptionCard/SubscriptionUtils.js';
import { Loading } from '../Loading';
import { getAddToCartEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import { addSubscriptionToShoppingBasket } from '../ShoppingBasket/shoppingBasketFunctions.js';
import { getBundledOfferAddons } from '../ShoppingBasket/shoppingBasketHelpers';
import { getAddedAddOns } from '../SubscriptionDetails/addOnDependencyUtils.js';
import { CardSelectionHeader } from './CardSelectionHeader.js';
import { Disclaimer } from './Disclaimer.js';
import { LegacyOffersSelection } from './LegacyOffersSelection.js';
import { OffersNotAvailable } from './OffersNotAvailable.js';
import {
  getCardContentWithServiceFee,
  getLegacyOffersForDropdownDisplay,
  getSelectedServiceFee,
  getSubscriptionCardByLegacyOffer,
  getSubscriptionCardStaticContent,
  isFixedPeriodActive,
  prepareOfferAndServiceFeeMap,
} from './OrderSubscriptionSelectionUtils';
import { SubscriptionCardsByModelType } from './SubscriptionCardsByModelType.js';
import { SubscriptionSelectionFooter } from './SubscriptionSelectionFooter.js';
import { Voucher } from './Voucher';
import { commercialProductContent } from './content/CommercialProductContent.js';
import { getMobileM2MCardContents4G, getMobileM2MCardContents5G } from './content/LaitenettiSubscriptionCardContent';
import { MinuteOfferType } from './minuteBasedSubscriptionUtil.js';
import { getSubscriptionCardContent } from './subscriptionProductCardUtils.js';
import type { ProductContent } from './content/CommercialProductContent.js';
import type { MinuteSubscription } from './minuteBasedSubscriptionUtil.js';
import type { OfferPerGroup } from './subscriptionProductCardUtils.js';
import type { EcommerceItem } from '../../common/analytics.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type {
  AddOnRulesResponse,
  CampaignAssociation,
  CampaignPromotion,
  CommercialProduct,
  Offer,
  OnlineModel,
  Subscription,
} from '../../generated/api/models.js';
import type { AssociationRecord } from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';
import type { State } from '../../selfservice/common/store.js';
import type {
  SubCardWithAnalyticsV4Props,
  SubCardWithImpressionProps,
} from '../HorizontalSubscriptionCard/subscriptionCardsAnalyticsUtils.js';
import type { SubCardProps } from '../SubscriptionCard/SubscriptionCard.js';

import './OrderSubscriptionSelection.scss';

interface OfferCardContentProps extends ProductContent {
  body: JSX.Element;
}

export interface OfferCard {
  campaignAssociation: CampaignAssociation | undefined;
  campaignPromotion: CampaignPromotion | undefined;
  content: OfferCardContentProps;
  offer: Offer;
  onlineModel: OnlineModel;
  voucherInUse: boolean;
  voucher?: string;
}

export interface OrderSubscriptionSelectionCommonProps {
  minuteModels?: OnlineModel[];
  onlineModelType: ModelType;
  subscription?: Subscription;
  onlineModels?: OnlineModel[];
  addOnRules?: AddOnRulesResponse;
  hideYrityspuheUUSI?: boolean;
  visibleOffers?: Array<{ offerCodes: string[]; onlineModelCode: string }>;
  eventOrigin: string;
}

type OrderSubscriptionSelectionProps = OrderSubscriptionSelectionCommonProps &
  (
    | { showOnlyMinuteAndLegacy: true; isChangeOffer: false }
    | { showOnlyMinuteAndLegacy: false; isChangeOffer: true; subscription: Subscription }
  );
export const getOnlineModel = (
  modelType: ModelType,
  addonAssociations: ReadonlyArray<AssociationRecord> = [],
  isChangeOffer: boolean,
  onlineModels?: Readonly<OnlineModel[]>
): OnlineModel | undefined => {
  const onlineModel = onlineModels?.find(item => item.onlineModelCode === modelType);
  if (modelType === ModelType.MobileM2M && onlineModel) {
    // Laitenetti has 2 online-models, 1st for 4G where 1 offer is used to create 3, and 2nd for 5G that's used as 1. 2 to 4 in total.
    const content = [getMobileM2MCardContents4G(isChangeOffer), getMobileM2MCardContents5G(isChangeOffer)];
    const offers = content.flatMap((item, index) =>
      item
        .map(product => {
          const addonAssoc = findActiveAddonAssociation(addonAssociations, product.selectedAddon!, 'Datapaketit');
          if (!addonAssoc) {
            return undefined;
          }

          const addon = newAddon(addonAssoc, 'Internet');
          const offer =
            onlineModel.offers.find(off => off.offerCode === product.selectedOffer) || onlineModel.offers[index];
          const cp = offer?.commercialProducts[0];
          const newOffer: Offer = {
            ...offer,
            offerName: addon.addOnProductName,
            commercialProducts: [
              {
                ...cp,
                commercialProductName: addon.addOnProductName,
                associatedAddOns: [...(cp?.associatedAddOns || []), addon],
                addOnAssociations: [
                  ...(cp?.addOnAssociations || []),
                  {
                    addOnAssociationId: addonAssoc.id,
                    addOnCode: addonAssoc.cspmb__add_on_price_item__r.guid__c,
                    addOnAssociationCode: addonAssoc.guid__c,
                    addOnPurpose: AddOnPurpose.MANDATORY,
                    display: false,
                  },
                ],
              },
            ],
          };
          return newOffer;
        })
        .filter(isDefined)
    );

    // The mangled OnlineModel we return below contains original `addOns` field, even though we've added new offers,
    // which may contain new addons as well. We don't bother to add those as they serve no purpose.
    return {
      ...onlineModel!,
      offers,
    };
  } else {
    return onlineModel;
  }
};

export const getAddOns = (
  addOnCodes: string[],
  selectedOffer: Readonly<Offer>,
  addOnAssociations?: AssociationRecord[]
): AddedAddon[] => {
  // the add-on-rules object fields, since it is not typed (yet)...
  const addOnPriceItem = 'cspmb__add_on_price_item__r';
  const guid = 'guid__c';
  const priceItem = 'cspmb__price_item__r';
  const priceItemDescription = 'cspmb__add_on_price_item_description__c';
  const recurringCharge = 'cspmb__recurring_charge__c';
  const oneOffCharge = 'cspmb__one_off_charge__c';
  const theAddOns: AddedAddon[] = [];
  if (addOnAssociations) {
    addOnCodes.forEach(addOnCode => {
      if (addOnCode) {
        const addOn = addOnAssociations?.find(
          aos =>
            aos[addOnPriceItem] &&
            aos[addOnPriceItem][guid] === addOnCode &&
            aos[priceItem] &&
            aos[priceItem][guid] === selectedOffer.commercialProducts[0].commercialProductCode
        );

        if (addOn) {
          const addOnObj: AddedAddon = {
            addOnAssociationCode: addOn[guid],
            addOnCode: addOn[addOnPriceItem][guid],
            displayName: addOn[addOnPriceItem][priceItemDescription],
            monthlyPrice: eurosToCents(addOn[addOnPriceItem][recurringCharge]),
            oneTimePrice: addOn[addOnPriceItem][oneOffCharge]
              ? eurosToCents(addOn[addOnPriceItem][oneOffCharge])
              : undefined,
          };
          theAddOns.push(addOnObj);
        }
      }
    });
  }
  return theAddOns;
};

export const createEcommerceItemFromProduct = (
  commercialProduct: Readonly<CommercialProduct>,
  offer: Offer,
  isVoice: boolean,
  isMinuteBased: boolean,
  eventOrigin: string,
  index?: number
): EcommerceItem => ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_brand: 'Elisa',
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_category: commercialProduct.productSubType || commercialProduct.productType,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_id: offer.offerCode,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_list_name: `${eventOrigin} ${isVoice ? AnalyticsListNames.VOICE_LIST_NAME : AnalyticsListNames.MBB_YRITYSDATA_LIST_NAME}: ${
    isMinuteBased ? AnalyticsListNames.MINUTE_BASED_LIST_NAME : ''
  }Subscription Selection`,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_name: commercialProduct.commercialProductName,
  // position might change as the models are loaded separately, so let's not send that
  // eslint-disable-next-line @typescript-eslint/naming-convention
  item_variant: commercialProduct.commercialProductCode,
  index: index,
});

export const groupSubsByGroupHeader = (
  subs: (SubCardProps | SubCardWithImpressionProps | SubCardWithAnalyticsV4Props)[]
) => {
  return Array.from(
    subs.reduce((map, item) => {
      const group = item.groupHeader || '';
      if (!map.has(group)) {
        map.set(group, []);
      }
      map.get(group)?.push(item);
      return map;
    }, new Map<string, typeof subs>())
  ).map(([_, value]) => value);
};

// TODO move pure functions to outside the component?
export const OrderSubscriptionSelection = (props: Readonly<OrderSubscriptionSelectionProps>) => {
  const {
    onlineModelType,
    minuteModels,
    onlineModels,
    addOnRules,
    subscription,
    hideYrityspuheUUSI,
    visibleOffers,
    isChangeOffer,
    showOnlyMinuteAndLegacy,
    eventOrigin,
  } = props;
  const { state } = useLocation();
  const containerDivRef = createRef<HTMLDivElement>();
  const {
    campaignsFromVoucher = [],
    campaignContextsFromVoucher = [],
    voucher,
  } = useSelector((reduxState: State) => {
    const campaigns = reduxState.campaigns;
    return {
      campaignsFromVoucher: campaigns?.campaigns,
      campaignContextsFromVoucher: campaigns?.campaignContexts,
      voucher: campaigns?.voucher,
    };
  }, deepEqual);
  const { authenticatedUser } = useAuth();

  const { priceAlteredModels, fetchModelsByCodes } = useOnlineModelsContext();

  useEffect(() => {
    if (showOnlyMinuteAndLegacy) {
      fetchModelsByCodes([ModelType.VoiceYrityspuhe, ModelType.VoicePerusliittyma4g, ModelType.Yrityspaketti]);
    }
  }, [fetchModelsByCodes, showOnlyMinuteAndLegacy, authenticatedUser?.activeAccountMasterId]);

  const minuteModelCodes = [ModelType.VoiceYrityspuhe, ModelType.VoicePerusliittyma4g];
  const contextMinuteModels = showOnlyMinuteAndLegacy
    ? filterByOnlineModelCodes(priceAlteredModels.onlineModelsByCode, minuteModelCodes)
    : [];
  const effectiveMinuteModels =
    showOnlyMinuteAndLegacy && contextMinuteModels.length > 0 ? contextMinuteModels : minuteModels;

  const [internalAddOnRules, setInternalAddOnRules] = useState<AddOnRulesResponse | undefined>(addOnRules);
  const effectiveAddOnRules = addOnRules ?? internalAddOnRules;
  const addOnAssociations = effectiveAddOnRules?.associations?.[0] as AssociationRecord[];

  useEffect(() => {
    if (showOnlyMinuteAndLegacy && !addOnRules && !internalAddOnRules) {
      fetchAddOnRules().then(rules => setInternalAddOnRules(rules));
    }
  }, [showOnlyMinuteAndLegacy, addOnRules, internalAddOnRules]);

  const foundOnlineModel = getOnlineModel(onlineModelType, addOnAssociations, isChangeOffer, onlineModels);
  const [showAllProducts, setShowAllProducts] = useState(true);

  // TODO see if it's feasible to not use useState at all, i.e. move all of this to location.state
  const [amount, setAmount] = useState<number>(state?.amount ?? 1);
  const [minuteSubObj, setMinuteSubObj] = useState<MinuteSubscription>(state?.minuteSubObj ?? {});
  const [selectedAddOns, setSelectedAddOns] = useState<AddedAddon[]>(state?.selectedAddOns ?? []);
  const [selectedProduct, setSelectedProduct] = useState<Offer | undefined>(state?.selectedProduct);
  const [selectedVoucher, setSelectedVoucher] = useState<string | undefined>(state?.selectedVoucher);

  const nettiModel = onlineModels?.find(omo => omo.onlineModelCode === ModelType.ElisaNetti);

  const campaignPromotions = [
    ...campaignsFromVoucher,
    ...(foundOnlineModel?.campaigns || []),
    ...(nettiModel?.campaigns || []),
  ];
  const offerCampaignAssociationAndPromotion = selectedProduct
    ? getOfferCampaignAssociationAndPromotion(
        selectedProduct,
        isChangeOffer ? SalesType.CHANGE_OFFER : SalesType.NEW_SALE,
        campaignContextsFromVoucher,
        voucher,
        campaignPromotions
      )
    : undefined;

  useEffect(() => {
    if (!selectedProduct && foundOnlineModel) {
      const productFromOnlineModel = foundOnlineModel.offers.find(
        offer =>
          commercialProductContent().find(
            content => content.isDefaultSelected === true && content.offerCodes.includes(offer.offerCode)
          ) !== undefined
      );
      if (productFromOnlineModel) {
        setSelectedProduct(productFromOnlineModel);
      }
    }
  }, [selectedProduct, foundOnlineModel]);

  const getOnlineModelForOffer = (offerCode: string | undefined): OnlineModel | undefined => {
    if (!offerCode) {
      return undefined;
    }
    const minuteModel = effectiveMinuteModels?.find(model => model.offers.some(offer => offer.offerCode === offerCode));
    if (minuteModel) {
      return minuteModel;
    }
    // Check regular online models (for legacy offers)
    return onlineModels?.find(model => model.offers.some(offer => offer.offerCode === offerCode));
  };

  const handleLegacyOfferSelection = (offer: Offer) => {
    setMinuteSubObj({});
    // Some legacy voice subscriptions should use the same default addons as new same speed subscriptions using cards.
    // The default addons are defined in the hard coded card data. Check if there is a card mapped, then get and set the needed addons.
    const subscriptionCard = getSubscriptionCardByLegacyOffer(offer.offerCode);
    if (subscriptionCard) {
      const includedAddOns: string[] =
        // The 'as string' type assertion is safe here because we filtered out undefined guids
        subscriptionCard.services?.filter(s => s.isIncluded && s.guid !== undefined).map(s => s.guid as string) ?? [];
      setSelectedAddOns(getAddedAddOns(includedAddOns, offer, addOnAssociations));
    } else {
      setSelectedAddOns([]);
    }
    setSelectedProduct(offer);
    setSelectedVoucher(undefined);
  };

  const handleAmountChanged = (value: number) => {
    setAmount(clampQuantity(value));
  };

  const handleNewOrder = () => {
    if (amount > 0 && selectedProduct) {
      const offerCampaignAssociation = offerCampaignAssociationAndPromotion?.campaignAssociation;
      const onlineModelForOffer = showOnlyMinuteAndLegacy
        ? (getOnlineModelForOffer(selectedProduct.offerCode) ?? foundOnlineModel)
        : foundOnlineModel;

      pushToDataLayer(
        // All products are the same, we can just pick the first in the array
        onlineModelForOffer
          ? getAddToCartEventForPlaceOrder(
              onlineModelForOffer.onlineModelName,
              selectedProduct,
              amount,
              onlineModelForOffer,
              selectedAddOns,
              voucher,
              offerCampaignAssociation
            )
          : getAddToCartAnalyticsEvent('', selectedProduct, amount, selectedAddOns, voucher, offerCampaignAssociation)
      );

      // New checkout flow - add to shopping basket
      if (onlineModelForOffer) {
        const commercialProduct = selectedProduct.commercialProducts[0];
        const mappedAddOns = getBundledOfferAddons(commercialProduct);

        const associatedOfferAddons = mappedAddOns?.map(a => a.addOnCode) || [];

        // Filter out bundled addons from selected addons to avoid duplicates
        const selectedAddOnCodes = selectedAddOns
          .map(addon => addon.addOnCode)
          .filter(code => !associatedOfferAddons.includes(code));

        addSubscriptionToShoppingBasket(
          onlineModelForOffer,
          selectedProduct,
          selectedProduct.commercialProducts[0],
          true,
          amount,
          [...associatedOfferAddons, ...selectedAddOnCodes],
          addOnAssociations ?? [],
          selectedVoucher
        );
      }
    }
  };

  const isProductSelected = (): boolean => {
    if (
      selectedProduct?.offerCode === MinuteOfferType.VoiceYrityspuhe ||
      selectedProduct?.offerCode === MinuteOfferType.VoiceYrityspuheEU
    ) {
      // both packages must be selected to continue...
      return Boolean(minuteSubObj.dataPackage) && Boolean(minuteSubObj.smsPackage);
    }
    return Boolean(selectedProduct);
  };

  const selectedCommercialProduct: CommercialProduct | undefined = selectedProduct?.commercialProducts[0];

  // Get content based on the onlineModelType
  const subscriptionCardContent = getSubscriptionCardStaticContent(onlineModelType, isChangeOffer);

  const [selectedOffersPerGroup, setSelectedOffersPerGroup] = useState<OfferPerGroup[]>(
    subscriptionCardContent.map(subscriptionCard => ({
      groupName: subscriptionCard.cardName ?? '',
      offerCode: subscriptionCard.selectedOffer ?? '',
      addonCode: subscriptionCard.selectedAddon ?? '',
    }))
  );

  const hideNewCards = hideYrityspuheUUSI ?? false;

  // Cards with new design
  const subscriptionCardContentAll: SubCardWithAnalyticsV4Props[] = hideNewCards
    ? []
    : getSubscriptionCardContent(
        isChangeOffer,
        {
          voucher,
          campaignContexts: campaignContextsFromVoucher,
        },
        campaignPromotions,
        subscriptionCardContent,
        selectedOffersPerGroup,
        setSelectedOffersPerGroup,
        selectedProduct,
        foundOnlineModel,
        subscription,
        nettiModel?.offers
      );

  const subscriptionCardContents = hideNewCards ? [] : subscriptionCardContentAll;

  const offerAndFeeMap = prepareOfferAndServiceFeeMap(
    isChangeOffer,
    addOnRules,
    onlineModels,
    nettiModel,
    subscription
  );
  const selectedServiceFee = getSelectedServiceFee(offerAndFeeMap, selectedCommercialProduct?.commercialProductId);
  const cardContentWithFee = getCardContentWithServiceFee(subscriptionCardContents, offerAndFeeMap, isChangeOffer);

  const group = groupSubsByGroupHeader(cardContentWithFee);
  const groupedSubscriptions = group.length > 0 ? group : [[]];

  const fixedPeriodActiveFlag = isFixedPeriodActive(isChangeOffer, subscriptionCardContentAll);
  const legacyOfferFixedPeriodActiveFlag =
    isChangeOffer && isCommitmentPeriodActive(subscription?.details?.commitmentEndDate);

  const allOfferCodes = visibleOffers?.flatMap(offer => offer.offerCodes) ?? [];

  const legacyOffers: Offer[] = getLegacyOffersForDropdownDisplay(
    legacyOfferFixedPeriodActiveFlag,
    onlineModels || [],
    subscription?.commercialProductCode,
    allOfferCodes
  );

  const previousOffer = getOfferByCommercialProductCode(
    onlineModels?.flatMap(om => om.offers).concat(nettiModel?.offers ?? []),
    subscription?.commercialProductCode
  );

  if (showOnlyMinuteAndLegacy) {
    return (
      <div className="of-order-subscription-selection">
        {legacyOffers.length > 0 && (
          <LegacyOffersSelection
            offers={legacyOffers}
            selectedServiceFee={selectedServiceFee?.price}
            selectedOffer={selectedProduct}
            setSelectedOffer={handleLegacyOfferSelection}
            salesType={SalesType.NEW_SALE}
            campaignContexts={campaignContextsFromVoucher}
            voucher={voucher}
            modelType={onlineModelType}
          />
        )}
        <SubscriptionSelectionFooter
          isProductSelected={isProductSelected()}
          isChangeOffer={false}
          selectedCommercialProduct={selectedCommercialProduct}
          amount={amount}
          selectedAddOns={selectedAddOns}
          selectedCampaignAssociation={offerCampaignAssociationAndPromotion?.campaignAssociation}
          handleAmountChanged={handleAmountChanged}
          handleSubmit={handleNewOrder}
          campaignDescription={offerCampaignAssociationAndPromotion?.campaignPromotion?.description}
          selectedServiceFee={selectedServiceFee}
          useBundledAddOnPricing
        />
      </div>
    );
  }

  return (
    <div ref={containerDivRef} id="of-order-subscription-selection" className="of-order-subscription-selection">
      <Grid>
        <div className={`${dsClass.MARGIN_TOP_6} of-product-selector-container ${dsClass.PADDING_BOTTOM_7}`}>
          <CardSelectionHeader onlineModelType={onlineModelType} />
          {foundOnlineModel ? (
            <>
              <div className="of-horizontal-product-cards-voucher">
                <Voucher onlineModelCode={foundOnlineModel.onlineModelCode} salesType={SalesType.CHANGE_OFFER} />
              </div>
              {groupedSubscriptions.map((subs, index) => {
                const groupKey = subs[0]?.mainHeader || index;

                return (
                  <React.Fragment key={groupKey}>
                    {index === 0 && fixedPeriodActiveFlag && legacyOfferFixedPeriodActiveFlag && <OffersNotAvailable />}
                    {subs.length > 0 && (
                      <SubscriptionCardsByModelType
                        subscriptionCards={subs}
                        onlineModelType={onlineModelType}
                        isChangeOffer={true}
                        offerAndFeeMap={offerAndFeeMap}
                        selectedOffersPerGroup={selectedOffersPerGroup}
                        onSelectOffer={(guid: string, groupName: string) =>
                          setSelectedOffersPerGroup([
                            ...selectedOffersPerGroup.filter(offer => offer.groupName !== groupName),
                            { groupName: groupName, offerCode: guid, addonCode: '' },
                          ])
                        }
                        eventOrigin={eventOrigin}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </>
          ) : (
            !hideNewCards && <Loading />
          )}
          {legacyOffers.length > 0 && (
            <LegacyOffersSelection
              offers={legacyOffers}
              selectedServiceFee={selectedServiceFee?.price}
              selectedOffer={selectedProduct}
              setSelectedOffer={handleLegacyOfferSelection}
              salesType={SalesType.CHANGE_OFFER}
              campaignContexts={campaignContextsFromVoucher}
              voucher={voucher}
              modelType={onlineModelType}
            />
          )}
          {subscriptionCardContents.length > 0 && (
            <Disclaimer handleShowAllProducts={() => setShowAllProducts(true)} showAllProducts={showAllProducts} />
          )}
        </div>
      </Grid>

      <SubscriptionSelectionFooter
        isProductSelected={isProductSelected()}
        isChangeOffer={true}
        selectedCommercialProduct={selectedCommercialProduct}
        amount={amount}
        selectedAddOns={selectedAddOns}
        selectedCampaignAssociation={offerCampaignAssociationAndPromotion?.campaignAssociation}
        subscription={subscription}
        campaignDescription={offerCampaignAssociationAndPromotion?.campaignPromotion?.description}
        selectedServiceFee={selectedServiceFee}
        offer={selectedProduct}
        previousOffer={previousOffer}
        voucher={selectedVoucher}
        onlineModelName={foundOnlineModel?.onlineModelName}
      />
    </div>
  );
};
